import {makeAutoObservable} from "mobx";
import {TimetableActionTypes} from "../models/timetable";
import {ValidatedValue} from "../../helpers/validatedValue";
import Validator from "../../helpers/validator";
import {errorMessages} from "../../helpers/errorMessage";
import i18n from "../../i18n/i18n";

export interface IActionInStore {
    clearAction: () => void;
    ferryId: ValidatedValue<string>;
    setFerryId: (ferryId: string) => void;
    actionType: ValidatedValue<TimetableActionTypes | string>;
    setActionType: (actionType: TimetableActionTypes) => void;
    voyageNo: ValidatedValue<string>;
    setVoyageNo: (voyageNo: string) => void;
    wharfId: ValidatedValue<string>;
    setWharfId: (wharfId: string) => void;
    notes: ValidatedValue<string>;
    setNotes: (notes: string) => void;
    arrivalDate: ValidatedValue<Date | null>;
    setArrivalDate: (arrivalDate: Date | null) => void;
    arrivalHour: ValidatedValue<Date | null>;
    setArrivalHour: (arrivalHour: Date | null) => void;
    departureDate: ValidatedValue<Date | null>;
    setDepartureDate: (departureDate: Date | null) => void;
    departureHour: ValidatedValue<Date | null>;
    setDepartureHour: (departureHour: Date | null) => void;
    actualArrivalDate: ValidatedValue<Date | null>;
    setActualArrivalDate: (actualArrivalDate: Date | null) => void;
    actualArrivalHour: ValidatedValue<Date | null>;
    setActualArrivalHour: (actualArrivalHour: Date | null) => void;
    actualDepartureDate: ValidatedValue<Date | null>;
    setActualDepartureDate: (actualDepartureDate: Date | null) => void;
    actualDepartureHour: ValidatedValue<Date | null>;
    setActualDepartureHour: (departureHour: Date | null) => void;
    routes: ValidatedValue<string[]>;
    setRoutes: (routes: string[]) => void;
    openParkingDate: ValidatedValue<Date | null>;
    setOpenParkingDate: (openParkingDate: Date | null) => void;
    openParkingHour: ValidatedValue<Date | null>;
    setOpenParkingHour: (openParkingHour: Date | null) => void;
    startLoadingDate: ValidatedValue<Date | null>;
    setStartLoadingDate: (startLoadingDate: Date | null) => void;
    startLoadingHour: ValidatedValue<Date | null>;
    setStartLoadingHour: (startLoadingHour: Date | null) => void;
    endLoadingDate: ValidatedValue<Date | null>;
    setEndLoadingDate: (endLoadingDate: Date | null) => void;
    endLoadingHour: ValidatedValue<Date | null>;
    setEndLoadingHour: (endLoadingHour: Date | null) => void;
    destinationPortId: ValidatedValue<string>;
    setDestinationPortId: (destinationPort: string) => void;
    hasOrderRegistered: boolean;
    setHasOrderRegistered: (hasOrderRegistered: boolean) => void;
}

export class ActionInStore implements IActionInStore {
    _ferryId: ValidatedValue<string>;
    _actionType: ValidatedValue<TimetableActionTypes | string>;
    _voyageNo: ValidatedValue<string>;
    _wharfId: ValidatedValue<string>;
    _notes: ValidatedValue<string>;
    _arrivalDate: ValidatedValue<Date | null>;
    _arrivalHour: ValidatedValue<Date | null>;
    _departureDate: ValidatedValue<Date | null>;
    _departureHour: ValidatedValue<Date | null>;
    _actualArrivalDate: ValidatedValue<Date | null>;
    _actualArrivalHour: ValidatedValue<Date | null>;
    _actualDepartureDate: ValidatedValue<Date | null>;
    _actualDepartureHour: ValidatedValue<Date | null>;
    _routes: ValidatedValue<string[]>;
    _openParkingDate: ValidatedValue<Date | null>;
    _openParkingHour: ValidatedValue<Date | null>;
    _startLoadingDate: ValidatedValue<Date | null>;
    _startLoadingHour: ValidatedValue<Date | null>;
    _endLoadingDate: ValidatedValue<Date | null>;
    _endLoadingHour: ValidatedValue<Date | null>;
    _destinationPortId: ValidatedValue<string>;
    _hasOrderRegistered: boolean;

    constructor() {
        makeAutoObservable(this);

        const timetableValidators = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const voyageNoMaxLength = 15;
        const correctVoyageNoValidators = {
            name: "voyageNoLengthIsCorrect",
            fn: () => Validator.maxLength(this.voyageNo.value, voyageNoMaxLength),
            errorMessage: () => i18n.t("timetable.errors.voyageNoLength")
        };

        const correctDateValidators = {
            name: "dateIsCorrect",
            fn: Validator.isCorrectDate,
            errorMessage: () => i18n.t("errorMessages.correctDate")
        };

        const dateIsNumberValidators = {
            name: "dateIsNumber",
            fn: Validator.isDateIsNumber,
            errorMessage: () => i18n.t("errorMessages.dateIsNumber")
        };

        const correctTimeValidators = {
            name: "timeIsCorrect",
            fn: Validator.isCorrectTime,
            errorMessage: () => i18n.t("errorMessages.correctTime")
        };

        this._ferryId = new ValidatedValue("", [timetableValidators]);
        this._actionType = new ValidatedValue("", [timetableValidators]);
        this._voyageNo = new ValidatedValue("", [correctVoyageNoValidators]);
        this._wharfId = new ValidatedValue("", [timetableValidators]);
        this._notes = new ValidatedValue("");
        this._arrivalDate = new ValidatedValue(null, [timetableValidators, correctDateValidators, dateIsNumberValidators]);
        this._arrivalHour = new ValidatedValue(null, [timetableValidators, correctTimeValidators]);
        this._departureDate = new ValidatedValue(null, [timetableValidators, correctDateValidators, dateIsNumberValidators]);
        this._departureHour = new ValidatedValue(null, [timetableValidators, correctTimeValidators]);
        this._actualArrivalDate = new ValidatedValue(null, [correctDateValidators, dateIsNumberValidators]);
        this._actualArrivalHour = new ValidatedValue(null, [correctTimeValidators]);
        this._actualDepartureDate = new ValidatedValue(null, [correctDateValidators, dateIsNumberValidators]);
        this._actualDepartureHour = new ValidatedValue(null, [correctTimeValidators]);
        this._routes = new ValidatedValue([], [timetableValidators]);
        this._openParkingDate = new ValidatedValue(null, [timetableValidators, correctDateValidators, dateIsNumberValidators]);
        this._openParkingHour = new ValidatedValue(null, [timetableValidators, correctTimeValidators]);
        this._startLoadingDate = new ValidatedValue(null, [timetableValidators, correctDateValidators, dateIsNumberValidators]);
        this._startLoadingHour = new ValidatedValue(null, [timetableValidators, correctTimeValidators]);
        this._endLoadingDate = new ValidatedValue(null, [timetableValidators, correctDateValidators, dateIsNumberValidators]);
        this._endLoadingHour = new ValidatedValue(null, [timetableValidators, correctTimeValidators]);
        this._destinationPortId = new ValidatedValue("", [timetableValidators]);
        this._hasOrderRegistered = false;
    }

    clearAction() {
        this._ferryId.setValue("");
        this._actionType.setValue(TimetableActionTypes.ARRIVAL);
        this._voyageNo.setValue("");
        this._wharfId.setValue("");
        this._notes.setValue("");
        this._arrivalDate.setValue(null);
        this._arrivalHour.setValue(null);
        this._departureDate.setValue(null);
        this._departureHour.setValue(null);
        this._actualArrivalDate.setValue(null);
        this._actualArrivalHour.setValue(null);
        this._actualDepartureDate.setValue(null);
        this._actualDepartureHour.setValue(null);
        this._routes.setValue([]);
        this._openParkingDate.setValue(null);
        this._openParkingHour.setValue(null);
        this._startLoadingDate.setValue(null);
        this._startLoadingHour.setValue(null);
        this._endLoadingDate.setValue(null);
        this._endLoadingHour.setValue(null);
        this._destinationPortId.setValue("");
        this._hasOrderRegistered = false;
    }

    get ferryId() {
        return this._ferryId;
    }

    setFerryId(ferryId: string) {
        this._ferryId.setValue(ferryId);
    }

    get actionType() {
        return this._actionType;
    }

    setActionType(actionType: TimetableActionTypes) {
        this._actionType.setValue(actionType);
    }

    get voyageNo() {
        return this._voyageNo;
    }

    setVoyageNo(voyageNo: string) {
        this._voyageNo.setValue(voyageNo);
    }

    get wharfId() {
        return this._wharfId;
    }

    setWharfId(wharfId: string) {
        this._wharfId.setValue(wharfId);
    }

    get notes() {
        return this._notes;
    }

    setNotes(notes: string) {
        this._notes.setValue(notes);
    }

    get arrivalDate() {
        return this._arrivalDate;
    }

    setArrivalDate(arrivalDate: Date | null) {
        this._arrivalDate.setValue(arrivalDate);
    }

    get arrivalHour() {
        return this._arrivalHour;
    }

    setArrivalHour(arrivalHour: Date | null) {
        this._arrivalHour.setValue(arrivalHour);
    }

    get departureDate() {
        return this._departureDate;
    }

    setDepartureDate(departureDate: Date | null) {
        this._departureDate.setValue(departureDate);
    }

    get departureHour() {
        return this._departureHour;
    }

    setDepartureHour(departureHour: Date | null) {
        this._departureHour.setValue(departureHour);
    }

    get actualArrivalDate() {
        return this._actualArrivalDate;
    }

    setActualArrivalDate(actualArrivalDate: Date | null) {
        this._actualArrivalDate.setValue(actualArrivalDate);
    }

    get actualArrivalHour() {
        return this._actualArrivalHour;
    }

    setActualArrivalHour(actualArrivalHour: Date | null) {
        this._actualArrivalHour.setValue(actualArrivalHour);
    }

    get actualDepartureDate() {
        return this._actualDepartureDate;
    }

    setActualDepartureDate(actualDepartureDate: Date | null) {
        this._actualDepartureDate.setValue(actualDepartureDate);
    }

    get actualDepartureHour() {
        return this._actualDepartureHour;
    }

    setActualDepartureHour(departureHour: Date | null) {
        this._actualDepartureHour.setValue(departureHour);
    }

    get routes() {
        return this._routes;
    }

    setRoutes(routes: string[]) {
        this._routes.setValue(routes);
    }

    get openParkingDate() {
        return this._openParkingDate;
    }

    setOpenParkingDate(openParkingDate: Date | null) {
        this._openParkingDate.setValue(openParkingDate);
    }

    get openParkingHour() {
        return this._openParkingHour;
    }

    setOpenParkingHour(openParkingHour: Date | null) {
        this._openParkingHour.setValue(openParkingHour);
    }

    get startLoadingDate() {
        return this._startLoadingDate;
    }

    setStartLoadingDate(startLoadingDate: Date | null) {
        this._startLoadingDate.setValue(startLoadingDate);
    }

    get startLoadingHour() {
        return this._startLoadingHour;
    }

    setStartLoadingHour(startLoadingHour: Date | null) {
        this._startLoadingHour.setValue(startLoadingHour);
    }

    get endLoadingDate() {
        return this._endLoadingDate;
    }

    setEndLoadingDate(endLoadingDate: Date | null) {
        this._endLoadingDate.setValue(endLoadingDate);
    }

    get endLoadingHour() {
        return this._endLoadingHour;
    }

    setEndLoadingHour(endLoadingHour: Date | null) {
        this._endLoadingHour.setValue(endLoadingHour);
    }

    get destinationPortId() {
        return this._destinationPortId;
    }

    setDestinationPortId(destinationPortId: string) {
        this._destinationPortId.setValue(destinationPortId);
    }

    get hasOrderRegistered() {
        return this._hasOrderRegistered;
    }

    setHasOrderRegistered(hasOrderRegistered: boolean) {
        this._hasOrderRegistered = hasOrderRegistered;
    }
}
